import { supabase } from '../supabase';
import { currentDivisionId } from '../stores/useFiltersStore';
import type { ShipmentBox, ShipmentTrackingSummary } from '../utils/types';
import {
  getPreSettlementShipmentBoxes,
  getShipmentBoxesForExport,
  getShipmentTrackingSummaries,
  normalizeShipmentReferenceKey,
  refreshShipmentTrackingJourney,
} from './shipments';

/** Joint cargo groups, rather than individual pallets/boxing rows. */
export type ArrivalCargo = Pick<
  ShipmentBox,
  | 'shipment_id'
  | 'reference_number'
  | 'container_number'
  | 'awb_number'
  | 'receiver'
  | 'exporter_receiver_id'
  | 'ship_name'
  | 'ship_type'
  | 'ship_etd'
  | 'ship_eta'
  | 'ship_destination_port'
  | 'ship_destination_country'
  | 'quantity'
  | 'box_weight'
  | 'box_format'
  | 'box_label'
  | 'variety'
  | 'caliber'
  | 'color'
  | 'category'
  | 'producer_name'
  | 'reception_date'
>;
export interface ArrivalData {
  cargo: ArrivalCargo[];
  tracking: Record<string, ShipmentTrackingSummary[]>;
  loadedAt: string;
}

export async function getArrivalData(
  exporterId: number,
  season: string,
  speciesId: number,
  signal?: AbortSignal,
  divisionId: number | null = currentDivisionId()
): Promise<ArrivalData> {
  const cargo: ArrivalCargo[] = [];
  let cursor = 0;
  const limit = 100;
  for (;;) {
    signal?.throwIfAborted();
    const { data, error } = await supabase.rpc(
      'get_shipment_arrival_cargo_page',
      {
        p_exporter_id: exporterId,
        p_season: season,
        p_species_id: speciesId,
        p_after_id: cursor,
        p_limit: limit,
        p_division_id: divisionId,
      }
    );
    signal?.throwIfAborted();
    // During the rolling migration, use the existing bounded boxing reader.
    // Permissions/timeouts/other failures must still fail the whole request.
    if (
      error?.code === 'PGRST202' ||
      (error?.code === '42883' &&
        error.message.includes('get_shipment_arrival_cargo_page'))
    ) {
      let legacy: ShipmentBox[] | null;
      try {
        legacy = await getPreSettlementShipmentBoxes(
          exporterId,
          season,
          speciesId,
          divisionId
        );
      } catch (fallbackError) {
        // Some databases predate both RPCs. Keep the same authenticated table
        // reader used for shipment exports; never bypass RLS or source scoping.
        if (
          typeof fallbackError !== 'object' ||
          fallbackError === null ||
          !('code' in fallbackError) ||
          (fallbackError.code !== 'PGRST202' &&
            !(
              fallbackError.code === '42883' &&
              'message' in fallbackError &&
              typeof fallbackError.message === 'string' &&
              fallbackError.message.includes('get_shipment_boxes_page')
            ))
        )
          throw fallbackError;
        signal?.throwIfAborted();
        legacy = await getShipmentBoxesForExport(exporterId, season, speciesId);
      }
      if (!legacy) throw new Error('No se pudieron cargar los arribos');
      cargo.length = 0;
      for (const box of legacy) cargo.push(box);
      break;
    }
    if (error) throw error;
    const page = data as {
      rows?: ArrivalCargo[];
      next_cursor?: number;
      has_more?: boolean;
    } | null;
    if (
      !page ||
      !Array.isArray(page.rows) ||
      typeof page.has_more !== 'boolean'
    ) {
      throw new Error('Respuesta de arribos inválida');
    }
    for (const row of page.rows) cargo.push(row);
    if (!page.has_more) break;
    if (
      !Number.isSafeInteger(page.next_cursor) ||
      page.next_cursor! <= cursor
    ) {
      throw new Error('Cursor de arribos inválido');
    }
    cursor = page.next_cursor!;
  }
  const references = [
    ...new Set(
      cargo.map((row) => normalizeShipmentReferenceKey(row.reference_number))
    ),
  ];
  const tracking: ArrivalData['tracking'] = {};
  // Bound request bodies and the event-summary hydration query as well.
  for (let offset = 0; offset < references.length; offset += 100) {
    signal?.throwIfAborted();
    const summaries = await getShipmentTrackingSummaries(
      exporterId,
      season,
      speciesId,
      references.slice(offset, offset + 100),
      divisionId
    );
    if (!summaries)
      throw new Error('No se pudo cargar el seguimiento de los arribos');
    for (const summary of summaries) {
      const existing = tracking[summary.reference_key] ?? [];
      tracking[summary.reference_key] = [
        ...new Map(
          [...existing, ...summary.tracking_journeys].map((journey) => [
            journey.id,
            journey,
          ])
        ).values(),
      ];
    }
  }
  return { cargo, tracking, loadedAt: new Date().toISOString() };
}

export async function refreshArrivalTracking(
  ids: string[],
  signal?: AbortSignal
) {
  const queue = [...new Set(ids)];
  const failures: Array<{ id: string; message: string }> = [];
  let succeeded = 0;
  let cursor = 0;
  await Promise.all(
    Array.from({ length: Math.min(3, queue.length) }, async () => {
      while (cursor < queue.length && !signal?.aborted) {
        const id = queue[cursor++];
        try {
          await refreshShipmentTrackingJourney(id);
          succeeded++;
        } catch (error) {
          failures.push({
            id,
            message:
              error instanceof Error ? error.message : 'Error de tracking',
          });
        }
      }
    })
  );
  return { succeeded, failures };
}

export type TrackingRouteStatus = 'PAST' | 'CURRENT' | 'FUTURE';
export type TrackingRouteFeature =
  | {
      type: 'Feature';
      geometry: { type: 'Point'; coordinates: [number, number] };
      properties: { status: TrackingRouteStatus; code?: string; name?: string };
    }
  | {
      type: 'Feature';
      geometry: { type: 'LineString'; coordinates: [number, number][] };
      properties: {
        status: TrackingRouteStatus;
        current: [number, number] | null;
      };
    };
export interface TrackingRoute {
  geojson: { type: 'FeatureCollection'; features: TrackingRouteFeature[] };
  syncedAt: string | null;
}

/**
 * Real routes and live positions (ShipsGo GeoJSON stored by the sync worker)
 * of the given journeys, keyed by journey id. Journeys without a stored
 * route are left out; before the column exists, nothing is returned.
 */
export async function getTrackingRoutes(
  journeyIds: string[],
  signal?: AbortSignal
): Promise<Record<string, TrackingRoute>> {
  const ids = [...new Set(journeyIds)];
  const routes: Record<string, TrackingRoute> = {};
  for (let offset = 0; offset < ids.length; offset += 100) {
    signal?.throwIfAborted();
    const { data, error } = await supabase
      .from('shipment_tracking_journeys')
      .select('id, route_geojson, route_synced_at')
      .in('id', ids.slice(offset, offset + 100))
      .not('route_geojson', 'is', null);
    signal?.throwIfAborted();
    // Rolling release: the column is added by migration 20261109120000.
    if (error?.code === '42703' || error?.code === 'PGRST204') return {};
    if (error) throw error;
    for (const row of data ?? []) {
      const geojson = row.route_geojson as TrackingRoute['geojson'] | null;
      if (geojson && Array.isArray(geojson.features))
        routes[row.id as string] = {
          geojson,
          syncedAt: (row.route_synced_at as string | null) ?? null,
        };
    }
  }
  return routes;
}
